/**
 * Health Insurance Card Section Component
 *
 * 保険証情報セクション - Health insurance card form section
 * Part of the Patient Edit screen (患者編集)
 */

import {
  useEffect,
  useRef,
  useState,
  useMemo,
  useCallback,
  forwardRef,
  useImperativeHandle,
  type ComponentRef,
  type MutableRefObject,
} from 'react';
import {
  UseFormRegister,
  UseFormWatch,
  UseFormSetValue,
  FieldErrors,
  Control,
  useFormState,
} from 'react-hook-form';
import { AppTextField } from '@/components/atoms';

import CollapsibleCard from '@/components/molecules/CollapsibleCard';
import useAppModal from '@/hooks/useAppModal';
import { useHealthInsuranceNormalization } from '@pages/main/patient/hooks';
import {
  PatientFormSchemaType,
  PATIENT_ADDRESS1_MAX_LENGTH,
  PATIENT_ADDRESS2_MAX_LENGTH,
  PATIENT_ADDRESS_KANA_MAX_LENGTH,
  HEALTH_INSURANCE_CARD_NAME_MAX_LENGTH,
  HEALTH_INSURANCE_CARD_NAME_KANA_MAX_LENGTH,
  validateHealthInsuranceCard,
  validateAgeForElderlyCertificate,
  // validateInsurerNumberPrefix, // Skipped: 保険者番号 is simple input + 保険者検索 only
  validateCardNumberForElderly,
  calculateAge,
} from '@pages/main/patient/types';
import { useJapaneseNameKanaFields } from '@/hooks/useJapaneseNameKanaFields';
import { usePatientMasterData } from '@/hooks/main/usePatientMasterData';
import {
  isInsuredSelfRelationship,
  normalizeHealthInsuranceRelationshipId,
} from '@pages/main/patient/utils/patientHistoryHelpers';
import { HealthInsuranceSectionForm, type CostRateChangeOption } from './HealthInsuranceSectionForm';
import { useHealthInsuranceHistoryMenu } from './HealthInsuranceHistoryMenu';
import { LATE_ELDERLY_INSURANCE_UNDER_65_CONFIRM } from '../constants/validationMessages';

/** 保険種別 後期高齢 (`insurance_types.id`). */
const LATE_ELDERLY_INSURANCE_TYPE_ID = 2;

type AppTextFieldRef = ComponentRef<typeof AppTextField>;

interface HealthInsuranceSectionProps {
  register: UseFormRegister<PatientFormSchemaType>;
  control: Control<PatientFormSchemaType>;
  watch: UseFormWatch<PatientFormSchemaType>;
  setValue: UseFormSetValue<PatientFormSchemaType>;
  errors: FieldErrors<PatientFormSchemaType>;
  isEditMode: boolean;
  isCurrent: boolean;
  onInsurerSearch: () => void;
  isInsurerSearchLoading?: boolean;
  onAddressSearch: () => void;
  onOpenMap: () => void;
  patientData?: import('../types').PatientFormData | null;
  onHistorySwitch?: (type: 'health_insurance_card', historyId: number) => void;
  onHistoryDelete?: (type: 'health_insurance_card', historyId: number) => void;
  /** A2: 履歴作成取消 — same as `/patients/edit/{id}` reset (shared with 受診者). */
  onHealthInsuranceHistoryCreateCancel?: () => void | Promise<void>;
  /** A2: `health_insurance_card_id` query present and empty = new 保険証履歴 draft. */
  healthInsuranceHistoryDraftByUrl?: boolean;
  /** Ref to 住所2 input — focused by AddressSearchModal after autofill. */
  address2Ref?: MutableRefObject<{ focus?: () => void } | null>;
  disabled?: boolean;
  /**
   * 続柄 ids the screen offers, when it offers fewer than the master holds.
   *
   * `relationships` has three rows — 1 本人, 2 家族, 3 「67(本人)」 — and RA2N's own
   * 受診者編集 screen renders all of them. A screen whose spec offers 「本人」 /
   * 「家族」 only passes `[1, 2]`: 3 is a legacy RA2 business code
   * (67 = a 公費 scheme) whose selection makes the cross-section rule demand an
   * insurer number starting with 67.
   *
   * The value the record already carries is always kept, whatever this says —
   * hiding it would leave the group with nothing selected and let a save
   * silently rewrite a legacy value.
   */
  allowedRelationshipIds?: number[];
  /** Fold the section from its header (「∧ 閉じる」). Off by default. */
  collapsible?: boolean;
  /**
   * Controls placed in the section's header bar, before the 履歴 menu — an
   * embedder's own action for the card (e.g. 資格情報取得). Nothing by default.
   */
  headerActions?: React.ReactNode;
  /**
   * Leave the 履歴 menu (履歴 ▾ / 履歴削除 / 履歴作成取消) out of the header — for an
   * embedder that does not wire the history handlers. Shown by default.
   */
  hideHistoryMenu?: boolean;
  /** Forwarded to the form — see `HealthInsuranceSectionForm`. */
  insurerNumberMaxLength?: number;
  /** Forwarded to the form — see `HealthInsuranceSectionForm`. */
  costRateChangeOptions?: ReadonlyArray<CostRateChangeOption>;
  /** Forwarded to the form — see `HealthInsuranceSectionForm`. */
  qualificationDateLabel?: string;
  /** Forwarded to the form — see `HealthInsuranceSectionForm`. */
  qualificationDateRequired?: boolean;
  /** Forwarded to the form — see `HealthInsuranceSectionForm`. */
  disableMapForInsuredPerson?: boolean;
  /**
   * Ask 「後期高齢者を設定できる年齢ではありませんが…」 when 後期高齢 is picked for a
   * 受診者 under 65; いいえ leaves 保険種別 as it was. RA2N's own screen asks on
   * save only; absent keeps that.
   */
  confirmLateElderlyAgeOnPick?: boolean;
  /** Forwarded to the form — see `HealthInsuranceSectionForm`. */
  keepInvalidTelephoneChars?: boolean;
  /** 被保険者名カナ holding a value the auto-fill did not write is not overwritten (see `useJapaneseNameKanaFields`). */
  keepExistingKana?: boolean;
  /** Forwarded to the form — see `HealthInsuranceSectionForm`. */
  copyInsuredSelfProfile?: boolean;
  /** Forwarded to the form — see `HealthInsuranceSectionForm`. */
  focusQualificationAfterInsurerNumber?: boolean;
  /**
   * The 受診者's 郵便番号 / 住所 / 電話番号 / 住所カナ reach the 被保険者 column only
   * while 続柄 is 本人 — they are then copied the moment 本人 is picked, and follow
   * the 受診者 as it is edited. RA2N's own screen mirrors them whatever 続柄
   * holds; absent keeps that.
   */
  mirrorPatientAddressForSelfOnly?: boolean;
}

const HealthInsuranceSection = forwardRef<
  {
    validateSection: (formData: PatientFormSchemaType) => boolean;
    focusCardNumber: () => void;
    focusCardMark: () => void;
  },
  HealthInsuranceSectionProps
>(
  (
    {
      register: _register,
      control,
      watch,
      setValue,
      errors,
      isEditMode,
      isCurrent,
      onInsurerSearch,
      isInsurerSearchLoading = false,
      onAddressSearch,
      onOpenMap,
      patientData,
      onHistorySwitch,
      onHistoryDelete,
      onHealthInsuranceHistoryCreateCancel,
      healthInsuranceHistoryDraftByUrl = false,
      address2Ref,
      disabled = false,
      allowedRelationshipIds,
      collapsible = false,
      headerActions,
      hideHistoryMenu = false,
      insurerNumberMaxLength,
      costRateChangeOptions,
      qualificationDateLabel,
      qualificationDateRequired,
      disableMapForInsuredPerson,
      confirmLateElderlyAgeOnPick = false,
      keepInvalidTelephoneChars = false,
      keepExistingKana = false,
      copyInsuredSelfProfile = false,
      mirrorPatientAddressForSelfOnly = false,
      focusQualificationAfterInsurerNumber = false,
    },
    ref
  ) => {
    const [, setValidationErrors] = useState<string[]>([]);
    const appModal = useAppModal();
    const cardNumberInputRef = useRef<AppTextFieldRef | null>(null);
    const cardMarkInputRef = useRef<AppTextFieldRef | null>(null);

    // Validation function that can be called externally
    const validateSection = (formData: PatientFormSchemaType): boolean => {
      const validation = validateHealthInsuranceCard(formData);
      setValidationErrors(validation.errors);
      if (!validation.isValid && validation.errors.length > 0) {
        // Show only the first error
        appModal.error({
          title: '入力エラー',
          content: validation.errors[0],
          okText: 'OK',
          cancelButtonProps: { style: { display: 'none' } },
        });
      }
      return validation.isValid;
    };

    // Clear errors function that can be called externally
    const clearErrors = (): void => {
      setValidationErrors([]);
    };

    // Expose validation function and clearErrors to parent component
    useImperativeHandle(ref, () => ({
      validateSection,
      clearErrors,
      focusCardNumber: () => cardNumberInputRef.current?.focus?.(),
      focusCardMark: () => cardMarkInputRef.current?.focus?.(),
    }));
    const relationshipId = watch('health_insurance_card.relationship_id');
    const insuranceTypeId = watch('health_insurance_card.insurance_type_id');
    const claimTypeId = watch('health_insurance_card.claim_type_id');
    const patientCostId = watch('health_insurance_card.patient_cost_id');
    const patientCostRate = watch('health_insurance_card.patient_cost_rate');
    const insurerCostRate = watch('health_insurance_card.cost_rate');
    const insurerFamilyCostRate = watch('health_insurance_card.family_cost_rate');
    const elderlyInsuranceCertificateId = watch(
      'health_insurance_card.elderly_insurance_certificate_id'
    );
    const insurerName = watch('health_insurance_card.insurer_name') || '';
    const costRateSelectionValue =
      elderlyInsuranceCertificateId && elderlyInsuranceCertificateId > 1
        ? `ei-${elderlyInsuranceCertificateId}`
        : patientCostId !== undefined && patientCostId !== null
          ? `pc-${patientCostId}`
          : 'pc-0';

    const getInitialPatientCostRate = useCallback(
      (nextRelationshipId?: number | null) => {
        const effectiveRelationshipId = nextRelationshipId ?? relationshipId;
        const baseRate = effectiveRelationshipId === 2 ? insurerFamilyCostRate : insurerCostRate;

        if (baseRate === undefined || baseRate === null) {
          return '';
        }
        return String(baseRate);
      },
      [relationshipId, insurerCostRate, insurerFamilyCostRate]
    );

    // const insurerNumber = watch('health_insurance_card.insurer_number'); // skipped: was for auto-type and prefix validation

    // Initialize normalization and field control hook
    const {
      getFieldReadonly,
      getFieldDisabled,
      getCardNumberLabel,
      handleCardMarkBlur,
      handleCardNumberBlur,
      resetInsuredPersonFromPatient,
    } = useHealthInsuranceNormalization({
      watch,
      setValue,
      relationshipId: normalizeHealthInsuranceRelationshipId(relationshipId),
    });

    // Master data from backend (lookup tables)
    const { data: masterData } = usePatientMasterData();
    const sexOptions = masterData?.sexes || [];
    const patientCostOptions = masterData?.patient_costs || [];
    const elderlyOptions = masterData?.elderly_insurance_certificates || [];
    const allRelationshipOptions = masterData?.relationships || [];
    const relationshipOptions = allowedRelationshipIds
      ? allRelationshipOptions.filter(
          option => allowedRelationshipIds.includes(option.id) || option.id === relationshipId
        )
      : allRelationshipOptions;

    // 本人 (id 1) のみ: 右カラム read-only（新規・複製・編集いずれも続柄で判定）
    const isInsuredPerson = isInsuredSelfRelationship(relationshipId);

    const insuredNameKana = watch('health_insurance_card.name_kana');
    const {
      nameInputProps: insuredNameInputProps,
      kanaInputProps: insuredNameKanaInputProps,
      maxLength: insuredNameKanaMaxLength,
      nameMaxLength: insuredNameMaxLength,
      syncLastKana: syncInsuredNameKanaLastRef,
    } = useJapaneseNameKanaFields({
      nameKanaValue: String(insuredNameKana ?? ''),
      setNameValue: v =>
        setValue('health_insurance_card.name', v, { shouldDirty: true, shouldValidate: false }),
      setKanaValue: v =>
        setValue('health_insurance_card.name_kana', v, {
          shouldDirty: true,
          shouldValidate: false,
        }),
      maxLength: HEALTH_INSURANCE_CARD_NAME_KANA_MAX_LENGTH,
      nameMaxLength: HEALTH_INSURANCE_CARD_NAME_MAX_LENGTH,
      fullWidthAlphaNumOnBlur: false,
      keepExistingKana,
    });

    useEffect(() => {
      syncInsuredNameKanaLastRef(String(insuredNameKana ?? ''));
    }, [insuredNameKana, syncInsuredNameKanaLastRef]);

    // Determine field states based on insurance type
    const isSeikatsuHogo = insuranceTypeId === 3; // 生活保護
    const isReimbursementInsurer = claimTypeId === 3;
    // const isJiyuuShinryou = insuranceTypeId === 4; // 自由診療 - TODO: Add special handling

    // Track previous insurance type to detect changes
    const [prevInsuranceTypeId, setPrevInsuranceTypeId] = useState<number | undefined>(
      insuranceTypeId
    );
    const insuranceTypeChangedByUserRef = useRef(false);

    // Clear related fields when insurance type changes
    // Per spec section 12.2: when switching insurance type, clear dependent fields
    useEffect(() => {
      if (prevInsuranceTypeId !== undefined && prevInsuranceTypeId !== insuranceTypeId) {
        const isUserTypeChange = insuranceTypeChangedByUserRef.current;
        insuranceTypeChangedByUserRef.current = false;

        if (!isUserTypeChange) {
          setPrevInsuranceTypeId(insuranceTypeId);
          return;
        }

        const insurerNum = watch('health_insurance_card.insurer_number') || '';

        // Don't clear insurer_number if the type change is auto-triggered by insurer number prefix
        // e.g., changing from 1 to 2 (後期高齢) because insurer_number starts with 39
        const isAutoTriggeredChange =
          (insuranceTypeId === 2 && insurerNum.startsWith('39')) ||
          (insuranceTypeId === 3 && insurerNum === '00000001');

        // Clear insurer information when switching insurance type
        // However, if auto-triggered by insurer number, preserve user's cost selections
        if (!isAutoTriggeredChange) {
          // User explicitly changed insurance type - clear all dependent fields
          setValue('health_insurance_card.insurer_number', '');
          setValue('health_insurance_card.insurer_name', '');
          // 後期高齢 names its own kind (useHealthInsuranceNormalization sets it on the same
          // switch); clearing it here ran after that and left the box empty.
          setValue(
            'health_insurance_card.insurer_kind_name',
            insuranceTypeId === LATE_ELDERLY_INSURANCE_TYPE_ID ? '後期高齢' : ''
          );
          setValue('health_insurance_card.patient_cost_rate', '');
          setValue('health_insurance_card.patient_cost_id', undefined);
          setValue('health_insurance_card.elderly_insurance_certificate_id', undefined);
          setValue('health_insurance_card.cost_rate', undefined);
          setValue('health_insurance_card.family_cost_rate', undefined);
          setValue('health_insurance_card.card_mark', '');
          setValue('health_insurance_card.card_number', '');
        }
      }
      setPrevInsuranceTypeId(insuranceTypeId);
    }, [insuranceTypeId, prevInsuranceTypeId, setValue, watch]);

    // Calculate patient cost rate moved to radio group onChange handlers to prevent
    // race conditions and unwanted state wipes when set externally (e.g. by Korei Modal)

    useEffect(() => {
      const hasPatientCost = patientCostId !== undefined && patientCostId !== null;
      const hasElderlyCert =
        elderlyInsuranceCertificateId !== undefined && elderlyInsuranceCertificateId !== null;

      if (!hasPatientCost && !hasElderlyCert) {
        const initialRate = getInitialPatientCostRate();
        setValue('health_insurance_card.patient_cost_id', 0);
        setValue('health_insurance_card.elderly_insurance_certificate_id', 1);
        if (initialRate) {
          setValue('health_insurance_card.patient_cost_rate', initialRate);
        }
      }

      // 初期値 restores insurer-default burden rate by 続柄, but only when the
      // form has no saved/displayed rate yet. A saved "0" is valid for 生活保護.
      const hasLoadedPatientCostRate =
        patientCostRate !== undefined && patientCostRate !== null && String(patientCostRate) !== '';
      if (
        patientCostId === 0 &&
        (elderlyInsuranceCertificateId ?? 0) <= 1 &&
        !hasLoadedPatientCostRate
      ) {
        const initialRate = getInitialPatientCostRate();
        if (initialRate) {
          setValue('health_insurance_card.patient_cost_rate', initialRate);
        }
      }
    }, [
      patientCostId,
      patientCostRate,
      elderlyInsuranceCertificateId,
      getInitialPatientCostRate,
      setValue,
    ]);

    // Initialize insurer kind name from patient data on mount
    useEffect(() => {
      if (isCurrent && patientData?.health_insurance_cards?.[0]?.insurer_kind_name) {
        setValue(
          'health_insurance_card.insurer_kind_name',
          patientData.health_insurance_cards[0].insurer_kind_name
        );
      }
    }, [isCurrent, patientData?.health_insurance_cards?.[0]?.insurer_kind_name, setValue]);

    // Initialize insurance name from patient information on component mount
    useEffect(() => {
      const currentInsuranceName = watch('health_insurance_card.name');
      const currentRelationshipId = watch('health_insurance_card.relationship_id');
      const currentPatientName = watch('patient_information.name');

      // Only auto-fill if insurance name is empty and patient name exists and relationship is set
      if (!currentInsuranceName && currentRelationshipId && currentPatientName) {
        let filledName = '';
        if (currentRelationshipId === 1 || currentRelationshipId === 3) {
          filledName = currentPatientName;
        } else if (currentRelationshipId === 2) {
          filledName = currentPatientName.split(/[\s　]+/)[0] || currentPatientName;
        }
        if (filledName) {
          setValue('health_insurance_card.name', filledName);
        }
      }
    }, [isEditMode]); // Run only on mount/unmount

    // Watch additional fields for validation
    const patientDateOfBirth = watch('patient_information.date_of_birth');

    const pickInsuranceType = (nextTypeId: number, apply: () => void) => {
      const age = patientDateOfBirth ? calculateAge(patientDateOfBirth) : null;
      if (nextTypeId !== LATE_ELDERLY_INSURANCE_TYPE_ID || age === null || age >= 65) {
        apply();
        return;
      }
      appModal.confirm({
        title: '確認',
        content: LATE_ELDERLY_INSURANCE_UNDER_65_CONFIRM,
        okText: 'はい',
        cancelText: 'いいえ',
        onOk: apply,
      });
    };
    const cardNumber = watch('health_insurance_card.card_number');
    const patientPostalCode1 = watch('patient_information.postal_code1');
    const patientPostalCode2 = watch('patient_information.postal_code2');
    const patientAddress1 = watch('patient_information.address1');
    const patientAddress2 = watch('patient_information.address2');
    const patientAddress3 = watch('patient_information.address3');
    const patientAddressName = watch('patient_information.address_name');
    const patientAddressKana = watch('patient_information.address_kana');
    const patientTelephone = watch('patient_information.telephone');

    // Only mirror patient → insurance fields when the source patient field was
    // edited by the user (dirty). form.reset() on patient reload does NOT mark
    // fields dirty, so saved 保険証 values are preserved — fixes the bug where
    // 家族 (続柄=2) with manually-entered 被保険者 address got overwritten with
    // the 受診者 address after reload.
    const { dirtyFields, defaultValues } = useFormState({ control, name: 'patient_information' });
    const patientDirty = dirtyFields.patient_information as Record<string, boolean> | undefined;

    /*
     * A field edited back to its loaded value is no longer dirty — retyping the
     * saved 「141」 passes through 「1」「14」 (dirty, mirrored) and ends on 「141」
     * (clean, not mirrored), leaving 「14」 on the 保険証 side. Once edited in this
     * load a field keeps mirroring; a form reset (a reload, a saved record, a
     * 履歴 switch — every one of them hands the form new default values) starts afresh.
     */
    const editedPatientFieldsRef = useRef(new Set<string>());
    useEffect(() => {
      editedPatientFieldsRef.current.clear();
    }, [patientData, defaultValues]);
    const wasEdited = (...fields: string[]): boolean => {
      fields.forEach(field => {
        if (patientDirty?.[field]) editedPatientFieldsRef.current.add(field);
      });
      if (mirrorPatientAddressForSelfOnly && !isInsuredPerson) return false;
      return fields.some(field => editedPatientFieldsRef.current.has(field));
    };

    // Auto-fill postal codes from patient information
    useEffect(() => {
      if (!wasEdited('postal_code1', 'postal_code2')) return;
      setValue('health_insurance_card.postal_code1', patientPostalCode1 || '');
      setValue('health_insurance_card.postal_code2', patientPostalCode2 || '');
    }, [patientPostalCode1, patientPostalCode2, patientDirty, setValue]);

    // Auto-fill address fields from patient information
    useEffect(() => {
      if (!wasEdited('address1', 'address2', 'address3', 'address_name')) return;
      setValue(
        'health_insurance_card.address1',
        (patientAddress1 || '').slice(0, PATIENT_ADDRESS1_MAX_LENGTH)
      );
      // address2 = 11.8 address2 + 11.11 建物・部屋 (address3) + 11.12 名称 (address_name).
      // Spec 12.18: do NOT include 住所カナ; address_kana is synced separately.
      const address2Parts: string[] = [];
      if (patientAddress2) address2Parts.push(patientAddress2);
      if (patientAddress3) address2Parts.push(patientAddress3);
      if (patientAddressName) address2Parts.push(patientAddressName);
      setValue(
        'health_insurance_card.address2',
        address2Parts.join(' ').slice(0, PATIENT_ADDRESS2_MAX_LENGTH)
      );
    }, [
      patientAddress1,
      patientAddress2,
      patientAddress3,
      patientAddressName,
      patientDirty,
      setValue,
    ]);

    // Auto-fill telephone from patient information (field 11.15)
    useEffect(() => {
      if (!wasEdited('telephone')) return;
      setValue('health_insurance_card.telephone', patientTelephone || '');
    }, [patientTelephone, patientDirty, setValue]);

    // Auto-fill address kana from patient information (field 11.16)
    useEffect(() => {
      if (!wasEdited('address_kana')) return;
      setValue(
        'health_insurance_card.address_kana',
        String(patientAddressKana || '').slice(0, PATIENT_ADDRESS_KANA_MAX_LENGTH)
      );
    }, [patientAddressKana, patientDirty, setValue]);

    // Name sync from patient → insurance is handled on blur in PatientInfoSection
    // (via syncPatientNameToInsuranceImmediate). No onChange sync here.

    // Validation: Age-based elderly certificate validation
    useEffect(() => {
      if (patientDateOfBirth && elderlyInsuranceCertificateId) {
        const age = calculateAge(patientDateOfBirth);
        const validation = validateAgeForElderlyCertificate(age, elderlyInsuranceCertificateId);
        if (!validation.isValid) {
          setValidationErrors(prev => {
            const newErrors = prev.filter(error => !error.includes('70歳未満'));
            return [...newErrors, validation.message!];
          });
        } else {
          setValidationErrors(prev => prev.filter(error => !error.includes('70歳未満')));
        }
      }
    }, [patientDateOfBirth, elderlyInsuranceCertificateId]);

    // Validation: Card number for elderly insurance
    useEffect(() => {
      if (insuranceTypeId && cardNumber) {
        // Skip 8-digit validation on change; only validate on official submit
        const validation = validateCardNumberForElderly(insuranceTypeId, cardNumber, false);
        if (!validation.isValid) {
          // Show error modal for elderly insurance with invalid card number
          appModal.error({
            title: '入力エラー',
            content: validation.message,
            okText: 'OK',
            cancelButtonProps: { style: { display: 'none' } },
          });
        }
      }
    }, [insuranceTypeId, cardNumber, appModal]);

    const insuranceTypeOptions = useMemo(
      () =>
        (masterData?.insurance_types as Array<{ id: number; name: string }> | undefined) || [
          { id: 1, name: '一般' },
          { id: 2, name: '後期高齢' },
          { id: 3, name: '生活保護' },
          { id: 4, name: '自由診療' },
        ],
      [masterData?.insurance_types]
    );

    const { historyMenu, showHealthInsuranceHistoryCreateCancel } =
      useHealthInsuranceHistoryMenu({
        isEditMode,
        isCurrent,
        disabled,
        patientData,
        watchedHealthInsuranceCardId: watch('health_insurance_card.id'),
        healthInsuranceHistoryDraftByUrl,
        onHistorySwitch,
        onHistoryDelete,
        onHealthInsuranceHistoryCreateCancel,
      });

    return (
      <CollapsibleCard
        title={`保険証${!isCurrent && isEditMode ? '（履歴表示）' : ''}`}
        collapsible={collapsible}
        extraHeaderContent={
          headerActions ? (
            <>
              {headerActions}
              {!hideHistoryMenu && historyMenu}
            </>
          ) : hideHistoryMenu ? undefined : (
            historyMenu
          )
        }
        headerBackgroundColor={
          showHealthInsuranceHistoryCreateCancel
            ? '#000'
            : !isCurrent && isEditMode
              ? '#6b7280'
              : undefined
        }
      >
        {/* Helper to block inputs when disabled prop is true (history creation for other sections) */}
        <div style={disabled ? { pointerEvents: 'none', opacity: 0.6 } : {}}>
          <HealthInsuranceSectionForm
            control={control}
            watch={watch}
            setValue={setValue}
            errors={errors}
            relationshipId={relationshipId}
            insuranceTypeId={insuranceTypeId}
            patientCostId={patientCostId}
            elderlyInsuranceCertificateId={elderlyInsuranceCertificateId}
            insurerName={insurerName}
            costRateSelectionValue={costRateSelectionValue}
            isReimbursementInsurer={isReimbursementInsurer}
            isSeikatsuHogo={isSeikatsuHogo}
            isInsuredPerson={isInsuredPerson}
            disabled={disabled}
            isInsurerSearchLoading={isInsurerSearchLoading}
            onInsurerSearch={onInsurerSearch}
            onAddressSearch={onAddressSearch}
            onOpenMap={onOpenMap}
            relationshipOptions={relationshipOptions}
            insuranceTypeOptions={insuranceTypeOptions}
            patientCostOptions={patientCostOptions}
            elderlyOptions={elderlyOptions}
            sexOptions={sexOptions}
            getInitialPatientCostRate={getInitialPatientCostRate}
            getFieldReadonly={getFieldReadonly}
            getFieldDisabled={getFieldDisabled}
            getCardNumberLabel={getCardNumberLabel}
            handleCardMarkBlur={handleCardMarkBlur}
            handleCardNumberBlur={handleCardNumberBlur}
            insuranceTypeChangedByUserRef={insuranceTypeChangedByUserRef}
            cardNumberInputRef={cardNumberInputRef}
            cardMarkInputRef={cardMarkInputRef}
            insuredNameMaxLength={insuredNameMaxLength}
            insuredNameKanaMaxLength={insuredNameKanaMaxLength}
            insuredNameInputProps={insuredNameInputProps}
            insuredNameKanaInputProps={insuredNameKanaInputProps}
            address2Ref={address2Ref}
            insurerNumberMaxLength={insurerNumberMaxLength}
            costRateChangeOptions={costRateChangeOptions}
            qualificationDateLabel={qualificationDateLabel}
            qualificationDateRequired={qualificationDateRequired}
            disableMapForInsuredPerson={disableMapForInsuredPerson}
            onInsuranceTypePick={confirmLateElderlyAgeOnPick ? pickInsuranceType : undefined}
            keepInvalidTelephoneChars={keepInvalidTelephoneChars}
            copyInsuredSelfProfile={copyInsuredSelfProfile}
            onInsuredSelfPick={mirrorPatientAddressForSelfOnly ? resetInsuredPersonFromPatient : undefined}
            focusQualificationAfterInsurerNumber={focusQualificationAfterInsurerNumber}
          />
        </div>
      </CollapsibleCard>
    );
  }
);

HealthInsuranceSection.displayName = 'HealthInsuranceSection';

export default HealthInsuranceSection;
